'use client';
import React from 'react';
import Link from 'next/link';
import PublicNav from '@/components/PublicNav';
import HomeFooter from '@/app/components/HomeFooter';
import { CheckCircle, Lock, ArrowLeft, MapPin, Globe, Clock, Briefcase } from 'lucide-react';

const candidateData = {
  firstName: 'Thandi',
  lastInitial: 'M.',
  province: 'Gauteng',
  jobType: 'Administration & Data Entry',
  score: 91,
  verified: true,
  bio: 'A dedicated and detail-oriented professional with experience in administrative support, data entry and office management. Known for reliability, strong communication skills and a willingness to learn new systems quickly.',
  skills: ['Administration', 'Data Entry', 'MS Office', 'Excel', 'Customer Service', 'Filing & Records'],
  languages: ['English', 'Zulu', 'Sotho'],
  availability: 'Available immediately',
  workTypes: ['Full-time', 'Contract'],
  drivingLicence: false,
  dimensions: [
    { name: 'Integrity', score: 92, weight: '30%' },
    { name: 'Energy', score: 88, weight: '25%' },
    { name: 'Learning Ability', score: 90, weight: '20%' },
    { name: 'Humility', score: 95, weight: '15%' },
    { name: 'Practical Readiness', score: 85, weight: '10%' },
  ],
};

const scoreColor = (score: number) => {
  if (score >= 90) return 'var(--charcoal)';
  if (score >= 75) return 'var(--amber)';
  return 'var(--charcoal)';
};

export default function CandidateProfilePage() {
  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      <div className="max-w-screen-xl mx-auto px-4 lg:px-8 py-8">
        <Link href="/directory" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
          <ArrowLeft size={15} />
          Back to Directory
        </Link>

        <div className="grid lg:grid-cols-[1fr_360px] gap-8">
          {/* Left: Profile */}
          <div>
            {/* Profile Header */}
            <div className="rounded-2xl p-7 mb-6" style={{ background: 'var(--amber)' }}>
              <div className="flex items-start gap-5">
                <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-3xl font-bold flex-shrink-0" style={{ background: 'var(--charcoal)', color: 'var(--amber)', fontFamily: 'var(--font-serif)' }}>
                  {candidateData.firstName.charAt(0)}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1 flex-wrap">
                    <h1 className="text-2xl font-bold" style={{ color: 'var(--charcoal)', fontFamily: 'var(--font-serif)' }}>
                      {candidateData.firstName} {candidateData.lastInitial}
                    </h1>
                    {candidateData.verified && (
                      <span className="flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full" style={{ background: 'var(--charcoal)', color: 'var(--amber)' }}>
                        <CheckCircle size={11} />
                        Verified
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 text-sm mb-2" style={{ color: 'var(--charcoal)', opacity: 0.7 }}>
                    <MapPin size={13} />
                    {candidateData.province}
                  </div>
                  <div className="text-sm font-medium" style={{ color: 'var(--charcoal)', opacity: 0.8 }}>{candidateData.jobType}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-3xl font-bold" style={{ color: 'var(--charcoal)', fontFamily: 'var(--font-serif)' }}>{candidateData.score}</div>
                  <div className="text-xs font-medium" style={{ color: 'var(--charcoal)', opacity: 0.6 }}>Work-Readiness Score</div>
                </div>
              </div>
            </div>

            {/* Bio */}
            <div className="rounded-2xl p-6 mb-5 border" style={{ borderColor: 'var(--border)' }}>
              <h2 className="font-bold text-base mb-3" style={{ color: 'var(--charcoal)' }}>About</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">{candidateData.bio}</p>
            </div>

            {/* Skills & Info */}
            <div className="grid sm:grid-cols-2 gap-4 mb-5">
              <div className="rounded-2xl p-5 border" style={{ borderColor: 'var(--border)' }}>
                <h3 className="font-bold text-sm mb-3" style={{ color: 'var(--charcoal)' }}>Skills</h3>
                <div className="flex flex-wrap gap-2">
                  {candidateData.skills?.map((skill) => (
                    <span key={skill} className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl p-5 border" style={{ borderColor: 'var(--border)' }}>
                <h3 className="font-bold text-sm mb-3" style={{ color: 'var(--charcoal)' }}>Details</h3>
                <div className="flex flex-col gap-2.5">
                  <div className="flex items-center gap-2 text-sm">
                    <Globe size={13} style={{ color: 'var(--muted-foreground)' }} />
                    <span className="text-muted-foreground">{candidateData.languages.join(', ')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock size={13} style={{ color: 'var(--muted-foreground)' }} />
                    <span className="text-muted-foreground">{candidateData.availability}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Briefcase size={13} style={{ color: 'var(--muted-foreground)' }} />
                    <span className="text-muted-foreground">{candidateData.workTypes.join(', ')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Work-Readiness Assessment */}
            <div className="rounded-2xl p-6 mb-5 border" style={{ borderColor: 'var(--border)' }}>
              <h2 className="font-bold text-base mb-4" style={{ color: 'var(--charcoal)' }}>Work-Readiness Assessment</h2>
              <div className="flex flex-col gap-3 mb-4">
                {candidateData.dimensions?.map((dim) => (
                  <div key={dim?.name} className="flex items-center gap-3">
                    <div className="w-28 text-xs font-medium text-muted-foreground">{dim?.name}</div>
                    <div className="flex-1 h-2 rounded-full" style={{ background: 'var(--muted)' }}>
                      <div className="h-2 rounded-full" style={{ background: scoreColor(dim?.score), width: `${dim?.score}%` }} />
                    </div>
                    <div className="w-6 text-xs font-bold text-right" style={{ color: scoreColor(dim?.score) }}>{dim?.score}</div>
                    <div className="w-8 text-xs text-muted-foreground">{dim?.weight}</div>
                  </div>
                ))}
              </div>
              <div className="text-xs text-muted-foreground">
                Overall Score: <span className="font-bold text-sm" style={{ color: 'var(--charcoal)' }}>{candidateData.score}/100</span>
              </div>
            </div>

            {/* Locked: Interview Summary */}
            <div className="rounded-2xl p-6 mb-5 relative overflow-hidden border" style={{ borderColor: 'var(--border)' }}>
              <h2 className="font-bold text-base mb-3" style={{ color: 'var(--charcoal)' }}>Interview Summary</h2>
              <div className="blur-sm select-none pointer-events-none">
                <p className="text-sm text-muted-foreground leading-relaxed mb-2">
                  Thandi demonstrated exceptional integrity throughout the assessment process. She was honest about her experience levels and showed a genuine desire to grow professionally. Her energy and enthusiasm were notable...
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Reference checks confirmed reliability and punctuality. Previous employer noted strong attention to detail and willingness to take on additional responsibilities...
                </p>
              </div>
              <div className="absolute inset-0 flex items-center justify-center rounded-2xl" style={{ background: 'rgba(255,255,255,0.7)' }}>
                <div className="flex items-center gap-2 text-sm font-bold" style={{ color: 'var(--charcoal)' }}>
                  <Lock size={16} />
                  Unlock to view full interview notes
                </div>
              </div>
            </div>
          </div>

          {/* Right: Unlock CTA */}
          <div className="lg:sticky lg:top-24 h-fit">
            <div className="rounded-2xl p-7 mb-4" style={{ background: 'var(--charcoal)' }}>
              <div className="flex items-center gap-2 mb-4">
                <Lock size={18} style={{ color: 'var(--amber)' }} />
                <h3 className="font-bold text-white" style={{ fontFamily: 'var(--font-serif)' }}>Unlock Full Profile</h3>
              </div>
              <div className="text-3xl font-bold text-white mb-1" style={{ fontFamily: 'var(--font-serif)' }}>R1,000</div>
              <div className="text-xs text-white/50 mb-5">One-time payment · Instant access</div>

              <div className="flex flex-col gap-2 mb-6">
                {[
                  'Full CV download',
                  'Phone number & email address',
                  'Verification information',
                  'Full interview notes',
                  'Assessment score breakdown',
                ]?.map((item) => (
                  <div key={item} className="flex items-center gap-2 text-sm text-white/80">
                    <CheckCircle size={13} style={{ color: 'var(--amber)' }} />
                    {item}
                  </div>
                ))}
              </div>

              <button
                className="w-full py-3.5 rounded-xl font-bold text-base transition-all duration-150 active:scale-95"
                style={{ background: 'var(--amber)', color: 'var(--charcoal)' }}
              >
                Unlock Profile — R1,000
              </button>
              <div className="text-xs text-white/40 text-center mt-3">
                PayFast · Peach Payments · Ozow
              </div>
            </div>

            {/* Locked info cards */}
            <div className="rounded-2xl p-5 border" style={{ borderColor: 'var(--border)' }}>
              <h4 className="font-bold text-sm mb-3" style={{ color: 'var(--charcoal)' }}>Locked Information</h4>
              <div className="flex flex-col gap-2">
                {['Surname', 'Phone Number', 'Email Address', 'Full CV', 'Reference Contact Details']?.map((item) => (
                  <div key={item} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Lock size={11} style={{ color: 'var(--muted-foreground)' }} />
                    {item}
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                Candidate contact details are only shared after profile purchase, in compliance with POPIA.
              </p>
            </div>
          </div>
        </div>
      </div>

      <HomeFooter />
    </div>
  );
}
